import { QueryClient } from '@tanstack/react-query';

import { ApiClientError } from '@terra/api-client';

/**
 * Extract a human-readable error message from any thrown value. Handles the
 * three common shapes: an axios-wrapped API error (`response.data.message`),
 * a plain Error, and a primitive (number, string).
 */
function readErrorMessage(err: unknown): string {
  if (err && typeof err === 'object') {
    const e = err as { response?: { data?: { message?: unknown } }; message?: unknown };
    const apiMessage = e.response?.data?.message;
    if (typeof apiMessage === 'string' && apiMessage.length > 0) return apiMessage;
    if (typeof e.message === 'string' && e.message.length > 0) return e.message;
  }
  return 'Something went wrong. Please try again.';
}

/**
 * Module-level fallback so every mutation that doesn't define its own
 * `onError` still surfaces failures to the user. We dispatch a custom event
 * rather than calling a toast library directly so the queryClient stays
 * decoupled from React; the Toaster component inside `VendorShell` listens
 * and renders.
 */
export function notifyError(message: string): void {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent('terra:notify', { detail: { kind: 'error', message } }));
}

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 15_000,
      retry: (n, e) => (e instanceof ApiClientError && !e.isRetryable ? false : n < 2),
      refetchOnWindowFocus: false,
    },
    mutations: {
      retry: false,
      // Default error handler — only fires when a mutation's own `onError`
      // doesn't return false / throw, so per-mutation handlers still win.
      onError: (err) => {
        console.error('mutation failed', err);
        notifyError(readErrorMessage(err));
      },
    },
  },
});
